import { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { UploadSimple, X } from "@phosphor-icons/react";

export default function Settings() {
  const [form, setForm] = useState(null);

  useEffect(() => {
    api.get("/settings").then((r) => setForm(r.data));
  }, []);

  if (!form) return <div className="p-10 text-sm text-gray-500">Memuat...</div>;

  const bind = (k) => ({
    value: form[k] || "",
    onChange: (e) => setForm({ ...form, [k]: e.target.value }),
  });

  const onLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500 * 1024) return toast.error("Ukuran logo maks 500KB");
    const reader = new FileReader();
    reader.onload = (ev) => setForm({ ...form, logo_url: ev.target.result });
    reader.readAsDataURL(file);
  };

  const submit = async () => {
    try {
      await api.put("/settings", form);
      toast.success("Pengaturan tersimpan");
    } catch (e) { toast.error("Gagal menyimpan"); }
  };

  return (
    <div>
      <PageHeader title="Pengaturan Showroom" subtitle="Logo, nama, alamat, dan tanda tangan di kwitansi" />
      <div className="p-4 md:p-6 lg:p-10 max-w-3xl">
        <div className="border border-gray-200 p-8 space-y-6">
          {/* Logo */}
          <div>
            <div className="text-[10px] uppercase tracking-widest text-gray-500 font-medium mb-3">Logo Showroom</div>
            <div className="flex items-start gap-6">
              <div className="w-32 h-32 border border-gray-200 flex items-center justify-center bg-gray-50 shrink-0">
                {form.logo_url ? (
                  <img src={form.logo_url} alt="Logo" className="max-w-full max-h-full object-contain" />
                ) : (
                  <div className="text-xs text-gray-400">Belum ada logo</div>
                )}
              </div>
              <div className="flex-1">
                <label className="inline-flex items-center gap-2 border border-gray-900 px-4 py-2 cursor-pointer hover:bg-gray-900 hover:text-white transition-colors text-xs uppercase tracking-wider" data-testid="logo-upload-label">
                  <UploadSimple size={14} /> Upload Logo
                  <input type="file" accept="image/png,image/jpeg,image/svg+xml" className="hidden" onChange={onLogo} data-testid="logo-upload-input" />
                </label>
                {form.logo_url && (
                  <button onClick={() => setForm({ ...form, logo_url: "" })} className="ml-3 inline-flex items-center gap-1 text-xs uppercase tracking-wider text-[#E63946] hover:underline" data-testid="logo-remove">
                    <X size={12} /> Hapus
                  </button>
                )}
                <div className="text-xs text-gray-500 mt-2">PNG / JPG / SVG · maks 500KB · akan tampil di kwitansi</div>
              </div>
            </div>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-widest text-gray-500 font-medium mb-3">Identitas</div>
            <div className="space-y-3">
              <div><Label className="text-xs uppercase tracking-wider">Nama Showroom</Label><Input {...bind("name")} className="mt-2 rounded-none" data-testid="set-name" /></div>
              <div><Label className="text-xs uppercase tracking-wider">Kota</Label><Input {...bind("city")} className="mt-2 rounded-none" /></div>
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-gray-500 font-medium mb-3">Alamat</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><Label className="text-xs uppercase tracking-wider">Rumah</Label><Textarea {...bind("address_home")} className="mt-2 rounded-none" rows={2} /></div>
              <div><Label className="text-xs uppercase tracking-wider">Telp Rumah</Label><Input {...bind("phone_home")} className="mt-2 rounded-none font-mono-num" /></div>
              <div><Label className="text-xs uppercase tracking-wider">Kantor</Label><Textarea {...bind("address_office")} className="mt-2 rounded-none" rows={2} /></div>
              <div><Label className="text-xs uppercase tracking-wider">Telp Kantor</Label><Input {...bind("phone_office")} className="mt-2 rounded-none font-mono-num" /></div>
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-gray-500 font-medium mb-3">WhatsApp Showroom</div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Nomor WA (format internasional)</Label>
              <Input {...bind("whatsapp_number")} placeholder="6281234567890" className="mt-2 rounded-none font-mono-num" data-testid="set-wa" />
              <div className="text-xs text-gray-500 mt-1">Tampil di halaman depan katalog publik untuk chat calon pembeli. Awali dengan 62 (bukan 08).</div>
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-gray-500 font-medium mb-3">Penanda Tangan Kwitansi</div>
            <div className="space-y-3">
              {[1, 2, 3].map((n) => (
                <div key={n} className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div><Label className="text-xs uppercase tracking-wider">Label #{n}</Label><Input {...bind(`signatory${n}_label`)} className="mt-2 rounded-none" /></div>
                  <div><Label className="text-xs uppercase tracking-wider">Nama #{n}</Label><Input {...bind(`signatory${n}_name`)} className="mt-2 rounded-none" data-testid={`set-sig${n}`} /></div>
                </div>
              ))}
            </div>
          </div>
          <Button onClick={submit} data-testid="settings-save" className="rounded-none bg-[#0A0A0A] hover:bg-gray-800 text-white uppercase tracking-wider text-xs h-11 w-full">
            Simpan Pengaturan
          </Button>
        </div>
      </div>
    </div>
  );
}
